import { GetStaticPathsResult, GetStaticPropsContext } from "next";
import { useTranslation } from "next-i18next";
import { BlogPostContent, BlogPostLayout } from "layouts/pages/BlogPostLayout";
import { useUTSLocaleContext } from "context/utsLocaleContext";
import { getBlogPost, getBlogPostsPaths } from "api/cms-website/cms-blog-posts";
import { CmsPagePreviewStatus } from "api/cms-website";
import { BlogPostProps } from "api/cms-website/cms-blog-posts.types";

import { serverSideTranslations } from "utils/next/translations";
import content from "content/blogPost";
import { getAppMode } from "utils/getAppMode";
import { I18nLocale, I18nToStrapiLocale, Theme } from "constants/enum";
import { checkIsNotFound } from "configs/unsupport-locale-routes";
import { UrlsConfig } from "configs/urlsConfig";
import { reportToSentry } from "utils/reportToSentry";

export interface BlogPageProps {
    post: BlogPostProps;
}

export type BlogPostParams = { id: string };

const { isDevMode, isPreliveMode } = getAppMode();

export default function BlogPage({ post }: BlogPageProps) {
    const { t } = useTranslation("global");
    const { locale } = useUTSLocaleContext();

    const pageContent = content(locale)(t, { locale });

    return (
        <BlogPostLayout content={pageContent as BlogPostContent} post={post} />
    );
}

export async function getStaticPaths(): Promise<
    GetStaticPathsResult<BlogPostParams>
> {
    const paths: Array<string | { params: BlogPostParams; locale?: string }> =
        [];

    if (isDevMode || isPreliveMode) {
        return {
            paths: [],
            fallback: "blocking",
        };
    }

    try {
        const data = await getBlogPostsPaths();

        if (!Array.isArray(data) || data?.length === 0) {
            return { paths, fallback: false };
        }

        paths.push(
            ...data.map((post) => ({
                params: { id: post.slug },
                locale: post.locale,
            }))
        );
    } catch (e) {
        const error = e as Error;
        reportToSentry({ error, extra: { ...error } });
        throw e;
    }

    return { paths, fallback: false };
}

export async function getStaticProps({
    params,
    locale,
}: GetStaticPropsContext) {
    if (checkIsNotFound(UrlsConfig.BLOG_POST, locale)) {
        return { notFound: true };
    }

    try {
        const localeToLanguage = I18nToStrapiLocale[locale as I18nLocale];
        const slugString = (params?.id as string) ?? "";

        const data = await getBlogPost({
            slugString,
            localeToLanguage,
            previewStatus: CmsPagePreviewStatus.PUBLISHED,
        });

        if (!data) {
            return { notFound: true };
        }

        const content = data;

        return {
            props: {
                post: content,
                ...(await serverSideTranslations(locale, [
                    "blog",
                    "navigationMenuRelaunch",
                ])),
                themeType: Theme.SYT,
            },
        };
    } catch (e) {
        const error = e as Error;
        reportToSentry({ error, extra: { ...error } });
        throw e;
    }
}
